/**
 * UnifiedMessages — /messages (V1.8.0)
 *
 * WhatsApp-Business-style unified inbox: staff see BOTH DaddyTalk
 * (candidate chats) and TeamTalk (internal staff chat) behind a
 * segmented tab switcher. Candidates see only the DaddyTalk view.
 *
 * URL contract:
 *   /messages                  → DaddyTalk tab (default)
 *   /messages?tab=daddytalk    → same
 *   /messages?tab=team         → TeamTalk tab
 *   /messages?tab=team&thread=xxx → deep-links a push notification click
 *
 * Notification deep-links: `/api/team-chat` sets url=`/messages?tab=team&thread=<id>`
 * and `/api/daddytalk` chat replies set url=`/messages?thread=<id>`.
 * Both surface here and route to the right inner component.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ChatCircleDots, UsersThree, DownloadSimple, X } from "@phosphor-icons/react";
import DaddyTalkInbox from "@/pages/DaddyTalkInbox";
import TeamTalkInbox from "@/pages/TeamTalkInbox";

const STAFF_ROLES = ["admin", "recruiter", "hiring_manager", "superadmin"];

export default function UnifiedMessages() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const isStaff = STAFF_ROLES.includes(user?.role);
    const rawTab = params.get("tab");
    const activeTab = isStaff && rawTab === "team" ? "team" : "daddytalk";

    // Unread badges — poll every 30s so the tab pills always show current counts.
    const [teamUnread, setTeamUnread] = useState(0);
    const [daddyUnread, setDaddyUnread] = useState(0);

    const loadBadges = useCallback(async () => {
        if (!isStaff) return;
        try {
            const [tt, dt] = await Promise.all([
                api.get("/team-chat/unread-count").catch(() => ({ data: { count: 0 } })),
                api.get("/daddytalk/threads?as_role=target").catch(() => ({ data: { items: [] } })),
            ]);
            setTeamUnread(tt?.data?.count || 0);
            setDaddyUnread((dt?.data?.items || []).filter((t) => t.has_unread).length);
        } catch { /* silent */ }
    }, [isStaff]);

    useEffect(() => {
        loadBadges();
        const t = setInterval(() => {
            if (document.visibilityState === "visible") loadBadges();
        }, 30000);
        return () => clearInterval(t);
    }, [loadBadges]);

    // Refresh badges on any TeamTalk / DaddyTalk SSE event — debounced
    // to 1/sec so a burst of events doesn't trigger a request stampede.
    useEffect(() => {
        let timer;
        const on = () => {
            if (timer) return;
            timer = setTimeout(() => { timer = null; loadBadges(); }, 1000);
        };
        window.addEventListener("teamtalk:message-new", on);
        window.addEventListener("teamtalk:read", on);
        window.addEventListener("daddytalk:message-new", on);
        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener("teamtalk:message-new", on);
            window.removeEventListener("teamtalk:read", on);
            window.removeEventListener("daddytalk:message-new", on);
        };
    }, [loadBadges]);

    function switchTab(tab) {
        const next = new URLSearchParams(params);
        if (tab === "daddytalk") next.delete("tab");
        else next.set("tab", tab);
        // Deep-link params (thread) belong to the inner tab — clear
        // them when the user manually flips so we don't try to open
        // a DaddyTalk thread id inside TeamTalk or vice versa.
        next.delete("thread");
        setParams(next, { replace: false });
    }

    // /messages is staff-only via App.js RoleGate. This defensive
    // guard is only reached if a future refactor expands the gate.
    if (!isStaff) {
        return (
            <div data-testid="unified-messages-candidate" className="flex-1 min-h-0 flex flex-col">
                <DaddyTalkInbox />
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 bg-[#f0f2f5] flex flex-col" data-testid="unified-messages">
            <InstallPWABanner />

            {/* WhatsApp-style light header + segmented tabs */}
            <div className="bg-[#128c7e] text-white px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                <div
                    role="tablist"
                    aria-label="Messages tabs"
                    className="inline-flex bg-white/15 backdrop-blur rounded-full p-0.5 relative"
                    data-testid="unified-messages-tabs"
                >
                    <TabPill
                        active={activeTab === "daddytalk"}
                        onClick={() => switchTab("daddytalk")}
                        icon={ChatCircleDots}
                        label="DaddyTalk"
                        sublabel="Candidates"
                        badge={daddyUnread}
                        testid="unified-tab-daddytalk"
                    />
                    <TabPill
                        active={activeTab === "team"}
                        onClick={() => switchTab("team")}
                        icon={UsersThree}
                        label="TeamTalk"
                        sublabel="Team"
                        badge={teamUnread}
                        testid="unified-tab-team"
                    />
                </div>
                <div className="text-[10px] font-medium text-white/70 hidden md:block">
                    Unified inbox
                </div>
            </div>

            {/* Active tab */}
            <div className="flex-1 min-h-0">
                {activeTab === "team" ? <TeamTalkInbox /> : <DaddyTalkInbox />}
            </div>
        </div>
    );
}

function TabPill({ active, onClick, icon: Icon, label, sublabel, badge, testid }) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            data-testid={testid}
            data-active={active ? "1" : "0"}
            className="relative inline-flex items-center gap-1.5 px-3 sm:px-5 py-2 min-w-[110px] justify-center rounded-full"
        >
            {active && (
                <motion.span
                    layoutId="tab-active-block"
                    transition={{ type: "spring", stiffness: 500, damping: 32 }}
                    className="absolute inset-0 bg-white rounded-full shadow-sm"
                />
            )}
            <span className={`relative z-10 inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${
                active ? "text-[#128c7e]" : "text-white/90 hover:text-white"
            }`}>
                <Icon size={14} weight={active ? "fill" : "bold"} />
                {label}
                <span className={`hidden md:inline text-[10px] font-normal ${
                    active ? "text-[#128c7e]/60" : "text-white/60"
                }`}>
                    · {sublabel}
                </span>
                {badge > 0 && (
                    <span
                        data-testid={`${testid}-badge`}
                        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold tabular-nums rounded-full ${
                            active ? "bg-emerald-500 text-white" : "bg-white text-[#128c7e]"
                        }`}
                    >
                        {badge > 99 ? "99+" : badge}
                    </span>
                )}
            </span>
        </button>
    );
}

/**
 * InstallPWABanner — sticky cobalt strip with a shifting shimmer sweep.
 * Fires when the browser advertises PWA installability. Hidden if
 * already installed OR dismissed in the last 7 days.
 */
function InstallPWABanner() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try {
            const raw = localStorage.getItem("pwa-install-dismissed-at");
            if (!raw) return false;
            return Date.now() - Number(raw) < 7 * 24 * 60 * 60 * 1000;
        } catch { return false; }
    });

    useEffect(() => {
        const standalone =
            window.matchMedia?.("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        setInstalled(standalone);

        const onBefore = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    if (installed || !deferredPrompt || dismissed) return null;

    async function install() {
        try {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome !== "accepted") {
                try { localStorage.setItem("pwa-install-dismissed-at", String(Date.now())); } catch {}
                setDismissed(true);
            }
        } finally { setDeferredPrompt(null); }
    }

    function dismiss() {
        try { localStorage.setItem("pwa-install-dismissed-at", String(Date.now())); } catch {}
        setDismissed(true);
    }

    return (
        <div
            className="relative bg-emerald-50 border-b border-emerald-200 px-4 sm:px-6 py-2.5 flex items-center gap-3"
            data-testid="unified-messages-install-banner"
        >
            <span className="inline-flex items-center justify-center w-8 h-8 bg-emerald-500 rounded-full text-white shrink-0">
                <DownloadSimple size={16} weight="bold" />
            </span>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-emerald-900">
                    Install Messages app
                </p>
                <p className="text-xs text-emerald-700/80 truncate">
                    Standalone · push notifications · one tap to your chats
                </p>
            </div>
            <button
                type="button"
                onClick={install}
                data-testid="unified-messages-install-btn"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-emerald-500 text-white text-xs font-semibold rounded-full hover:bg-emerald-600 active:scale-95 transition"
            >
                Install
            </button>
            <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                data-testid="unified-messages-install-dismiss"
                className="text-emerald-700/60 hover:text-emerald-900 p-1"
            >
                <X size={14} weight="bold" />
            </button>
        </div>
    );
}
